import {
  Description,
  MarkEmailReadOutlined,
  MarkEmailUnreadOutlined,
  OpenInNew,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import type { Task } from "fhir/r5";

import { isQuestionnaireTask } from "src-next/modules/questionnaire-builder/questionnaire-tasks";
import { isQuestionnaireAnsweredNotificationTask } from "@/utils/taskNotificationUtils";

import {
  formatNotificationDateTime,
  formatNotificationRelativeTime,
  getNotificationDetailText,
  getNotificationStatusLabel,
  isNotificationUnread,
} from "./notificationFormat";

interface NotificationItemCardProps {
  task: Task;
  accessType?: string | null;
  isUpdating: boolean;
  onToggleReadStatus: (task: Task) => void;
  onAccessMedicalRecord: (task: Task) => void;
  onRespondQuestionnaire: (task: Task) => void;
}

export function NotificationItemCard({
  task,
  accessType,
  isUpdating,
  onToggleReadStatus,
  onAccessMedicalRecord,
  onRespondQuestionnaire,
}: NotificationItemCardProps) {
  const theme = useTheme();
  const isPatientView = accessType === "patient";
  const isQ = isQuestionnaireTask(task);
  const isUnread = isNotificationUnread(task);
  const showPatientQuestionnaireOnly =
    isPatientView && isQ && task.status === "requested";
  const statusLabel = getNotificationStatusLabel(task, accessType);
  const dateSource = task.authoredOn || task.meta?.lastUpdated;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: isUnread
          ? alpha(theme.palette.warning.main, 0.4)
          : alpha(theme.palette.divider, 0.9),
        bgcolor: isUnread
          ? alpha(theme.palette.warning.main, 0.07)
          : "background.paper",
        boxShadow: isUnread
          ? `0 2px 10px ${alpha(theme.palette.warning.main, 0.1)}`
          : "none",
      }}
    >
      <CardContent sx={{ py: 1.75, px: 2, "&:last-child": { pb: 1.75 } }}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Box
            sx={{
              mt: 0.75,
              width: 9,
              height: 9,
              borderRadius: "50%",
              flexShrink: 0,
              bgcolor: isUnread ? "warning.main" : "transparent",
              border: isUnread
                ? "none"
                : `1.5px solid ${alpha(theme.palette.text.disabled, 0.35)}`,
            }}
          />

          <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography
                variant="subtitle2"
                fontWeight={isUnread ? 700 : 600}
                sx={{
                  lineHeight: 1.35,
                  wordBreak: "break-word",
                  color: "text.primary",
                }}
              >
                {task.description || "Tarefa sem descrição"}
              </Typography>

              {dateSource ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    fontSize: "0.6875rem",
                    pt: 0.15,
                  }}
                >
                  {formatNotificationRelativeTime(dateSource)}
                </Typography>
              ) : null}
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.45, wordBreak: "break-word" }}
            >
              {getNotificationDetailText(task)}
            </Typography>

            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              gap={0.75}
              sx={{ pt: 0.25 }}
            >
              <Chip
                label={statusLabel}
                size="small"
                color={
                  task.status === "completed"
                    ? "success"
                    : task.status === "in-progress"
                      ? "primary"
                      : "warning"
                }
                sx={{ height: 22, fontSize: "0.6875rem", fontWeight: 600 }}
              />
              {dateSource ? (
                <Typography variant="caption" color="text.disabled">
                  {formatNotificationDateTime(dateSource)}
                </Typography>
              ) : null}
            </Stack>

            {isUpdating ? (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ pt: 0.5 }}>
                <CircularProgress size={18} />
                <Typography variant="caption" color="text.secondary">
                  Atualizando...
                </Typography>
              </Stack>
            ) : showPatientQuestionnaireOnly ? (
              <Button
                fullWidth
                size="small"
                variant="contained"
                startIcon={<OpenInNew />}
                onClick={() => onRespondQuestionnaire(task)}
                sx={{
                  mt: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  py: 0.85,
                }}
              >
                Responder questionário
              </Button>
            ) : isPatientView && isQ ? null : (
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                {isQuestionnaireAnsweredNotificationTask(task) ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Description />}
                    onClick={() => onAccessMedicalRecord(task)}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: 2,
                    }}
                  >
                    Acessar prontuário
                  </Button>
                ) : null}

                <Button
                  size="small"
                  variant={isUnread ? "contained" : "text"}
                  color={isUnread ? "primary" : "inherit"}
                  startIcon={
                    isUnread ? (
                      <MarkEmailReadOutlined />
                    ) : (
                      <MarkEmailUnreadOutlined />
                    )
                  }
                  onClick={() => onToggleReadStatus(task)}
                  sx={{
                    flex: isQuestionnaireAnsweredNotificationTask(task)
                      ? 1
                      : undefined,
                    minWidth: isQuestionnaireAnsweredNotificationTask(task)
                      ? 0
                      : "100%",
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                  }}
                >
                  {isUnread ? "Marcar como lida" : "Marcar como não lida"}
                </Button>
              </Stack>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
